import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
  list: [],
  loanRequest: null,
  error: "",
  loading: false,
};

// CREATE LOAN REQUEST
export const createLoanRequest = createAsyncThunk(
  "loanRequest/createLoanRequest",
  async (values) => {
    try {
      const { data } = await axios({
        method: "post",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `loan-request/`,
        data: {
          ...values,
        },
      });
      toast.success("Loan Request Submitted Successfully");
      return {
        data,
        message: "success",
      };
    } catch (error) {
      if (error.response && error.response.data.message) {
        const errorMessage = error.response.data.message;
        toast.error(errorMessage);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      console.log(error.message);
      return {
        message: "error",
      };
    }
  }
);

// GET ALL LOAN REQUESTS
export const loadAllLoanRequests = createAsyncThunk(
  "loanRequest/loadAllLoanRequests",
  async () => {
    try {
      const { data } = await axios({
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `loan-request/`,
      });
      return data;
    } catch (error) {
      console.log(error.message);
      return [];
    }
  }
);

// GET LOAN REQUESTS BY USER ID
export const loadLoanRequestsByUserId = createAsyncThunk(
  "loanRequest/loadLoanRequestsByUserId",
  async (userId) => {
    try {
      const { data } = await axios({
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `loan-request/user/${userId}`,
      });
      return data;
    } catch (error) {
      console.log(error.message);
      return [];
    }
  }
);

// GET LOAN REQUESTS FOR MANAGER
export const loadLoanRequestsForManager = createAsyncThunk(
  "loanRequest/loadLoanRequestsForManager",
  async (managerId) => {
    try {
      const { data } = await axios({
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `loan-request/manager/${managerId}`,
      });
      return data;
    } catch (error) {
      console.log(error.message);
      return [];
    }
  }
);

// APPROVE LOAN REQUEST
export const approveLoanRequest = createAsyncThunk(
  "loanRequest/approveLoanRequest",
  async ({ id, values }) => {
    try {
      const { data } = await axios({
        method: "put",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `loan-request/${id}/approve`,
        data: {
          ...values,
        },
      });
      toast.success("Loan Request Approved Successfully");
      return {
        data,
        message: "success",
      };
    } catch (error) {
      if (error.response && error.response.data.message) {
        const errorMessage = error.response.data.message;
        toast.error(errorMessage);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      console.log(error.message);
      return {
        message: "error",
      };
    }
  }
);

// REJECT LOAN REQUEST
export const rejectLoanRequest = createAsyncThunk(
  "loanRequest/rejectLoanRequest",
  async ({ id, values }) => {
    try {
      const { data } = await axios({
        method: "put",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `loan-request/${id}/reject`,
        data: {
          ...values,
        },
      });
      toast.success("Loan Request Rejected");
      return {
        data,
        message: "success",
      };
    } catch (error) {
      if (error.response && error.response.data.message) {
        const errorMessage = error.response.data.message;
        toast.error(errorMessage);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      console.log(error.message);
      return {
        message: "error",
      };
    }
  }
);

// GET TEAM MEMBERS
export const getTeamMembers = createAsyncThunk(
  "loanRequest/getTeamMembers",
  async (managerId) => {
    try {
      const { data } = await axios({
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `user/team/${managerId}`,
      });
      return data;
    } catch (error) {
      console.log(error.message);
      return [];
    }
  }
);

// GET ALL EMPLOYEES (for admin/HR)
export const getAllEmployees = createAsyncThunk(
  "loanRequest/getAllEmployees",
  async () => {
    try {
      const { data } = await axios({
        method: "get",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json;charset=UTF-8",
        },
        url: `user?query=all`,
      });
      return data;
    } catch (error) {
      console.log(error.message);
      return [];
    }
  }
);

const loanRequestSlice = createSlice({
  name: "loanRequest",
  initialState,
  reducers: {
    clearLoanRequest: (state) => {
      state.loanRequest = null;
    },
  },
  extraReducers: (builder) => {
    // CREATE LOAN REQUEST
    builder.addCase(createLoanRequest.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createLoanRequest.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createLoanRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to create loan request";
    });

    // LOAD ALL LOAN REQUESTS
    builder.addCase(loadAllLoanRequests.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadAllLoanRequests.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload;
    });
    builder.addCase(loadAllLoanRequests.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to load loan requests";
    });

    // LOAD LOAN REQUESTS BY USER ID
    builder.addCase(loadLoanRequestsByUserId.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadLoanRequestsByUserId.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload;
    });
    builder.addCase(loadLoanRequestsByUserId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to load user loan requests";
    });

    // LOAD LOAN REQUESTS FOR MANAGER
    builder.addCase(loadLoanRequestsForManager.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadLoanRequestsForManager.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload;
    });
    builder.addCase(loadLoanRequestsForManager.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to load manager loan requests";
    });

    // APPROVE LOAN REQUEST
    builder.addCase(approveLoanRequest.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(approveLoanRequest.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(approveLoanRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to approve loan request";
    });

    // REJECT LOAN REQUEST
    builder.addCase(rejectLoanRequest.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rejectLoanRequest.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(rejectLoanRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to reject loan request";
    });

    // GET TEAM MEMBERS
    builder.addCase(getTeamMembers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTeamMembers.fulfilled, (state, action) => {
      state.loading = false;
      state.teamMembers = action.payload;
    });
    builder.addCase(getTeamMembers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to load team members";
    });

    // GET ALL EMPLOYEES
    builder.addCase(getAllEmployees.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllEmployees.fulfilled, (state, action) => {
      state.loading = false;
      state.allEmployees = action.payload;
    });
    builder.addCase(getAllEmployees.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to load employees";
    });
  },
});

export default loanRequestSlice.reducer;
export const { clearLoanRequest } = loanRequestSlice.actions;
